import React, { useState, useEffect, useCallback } from 'react';
import { Inbox, Download, ChevronDown, ChevronUp, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const fj = (url, opts = {}) => fetch(`${API}${url}`, { credentials: 'include', ...opts });
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '');

const STATO_STYLE = {
  nuova: 'bg-amber-100 text-amber-700',
  in_lavorazione: 'bg-blue-100 text-blue-700',
  gestita: 'bg-[#0E8C84]/15 text-[#0B7770]',
};
const STATO_LABEL = { nuova: 'Nuova', in_lavorazione: 'In lavorazione', gestita: 'Gestita' };

const richiedente = (s) => {
  const a = s.answers || {};
  return a.nome_ente || a.ente || a.contraente || a.cognome_nome || a.referente || '—';
};

export default function AdminRichiesteModuli() {
  const [subs, setSubs] = useState([]);
  const [labels, setLabels] = useState({});
  const [formLabels, setFormLabels] = useState({});
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fj('/api/forms/admin/submissions');
      if (res.ok) {
        const out = await res.json();
        setSubs(out.submissions || []);
        setLabels(out.labels || {});
        setFormLabels(out.form_labels || {});
      }
    } catch { /* silenzioso */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStato = async (sid, stato) => {
    try {
      const res = await fj(`/api/forms/admin/submissions/${sid}/stato`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ stato }),
      });
      if (res.ok) {
        setSubs((p) => p.map((s) => (s.id === sid ? { ...s, stato } : s)));
        toast.success('Stato aggiornato');
      } else toast.error('Errore');
    } catch { toast.error('Errore di connessione'); }
  };

  const deleteSub = async (sid) => {
    if (!window.confirm('Eliminare definitivamente questa richiesta e i suoi dati personali? L\'operazione non è reversibile.')) return;
    try {
      const res = await fj(`/api/forms/admin/submissions/${sid}`, { method: 'DELETE' });
      if (res.ok) {
        setSubs((p) => p.filter((s) => s.id !== sid));
        toast.success('Richiesta eliminata');
      } else toast.error('Errore');
    } catch { toast.error('Errore di connessione'); }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await fj(`/api/forms/admin/submissions-export${filter ? `?form=${filter}` : ''}`);
      if (!res.ok) { toast.error('Errore nell\'esportazione'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = (res.headers.get('Content-Disposition') || '').match(/filename="(.+)"/)?.[1] || 'richieste.xlsx';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('File Excel scaricato');
    } catch { toast.error('Errore di connessione'); } finally { setExporting(false); }
  };

  const shown = filter ? subs.filter((s) => s.form === filter) : subs;

  return (
    <div className="pt-8 border-t border-gray-200 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
          <Inbox size={18} className="text-[#0E8C84]" /> Richieste dai moduli
          {subs.filter((s) => s.stato === 'nuova').length > 0 && (
            <span data-testid="subs-new-count" className="text-[11px] font-bold bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">
              {subs.filter((s) => s.stato === 'nuova').length} nuove
            </span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          <select data-testid="subs-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
            <option value="">Tutti i moduli</option>
            {Object.entries(formLabels).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <button data-testid="subs-refresh" type="button" onClick={load} className="p-2 text-gray-400 hover:text-[#1B2380] border border-gray-200 rounded-lg"><RefreshCw size={15} /></button>
          <button data-testid="subs-export" type="button" onClick={exportExcel} disabled={exporting}
            className="inline-flex items-center gap-1.5 whitespace-nowrap bg-[#1B2380] hover:bg-[#2A3299] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-60">
            <Download size={14} /> {exporting ? 'Esportazione…' : 'Esporta Excel'}
          </button>
        </div>
      </div>

      {shown.length === 0 ? (
        <p data-testid="subs-empty" className="text-sm text-gray-400 bg-gray-50 border border-gray-200 rounded-xl p-5 text-center">Nessuna richiesta ricevuta dai moduli per ora.</p>
      ) : (
        <div className="space-y-2">
          {shown.map((s) => (
            <div key={s.id} className="bg-gray-50 border border-gray-200 rounded-xl" data-testid={`sub-row-${s.id}`}>
              <button type="button" data-testid={`sub-toggle-${s.id}`} onClick={() => setExpanded(expanded === s.id ? null : s.id)}
                className="w-full flex flex-wrap items-center justify-between gap-2 p-3.5 text-left">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">{richiedente(s)}</p>
                  <p className="text-xs text-gray-500">{formLabels[s.form] || s.form} · {fmtDate(s.created_at)}{(s.answers || {}).email ? ` · ${s.answers.email}` : ''}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATO_STYLE[s.stato] || STATO_STYLE.nuova}`}>{STATO_LABEL[s.stato] || 'Nuova'}</span>
                  {expanded === s.id ? <ChevronUp size={15} className="text-gray-400" /> : <ChevronDown size={15} className="text-gray-400" />}
                </div>
              </button>
              {expanded === s.id && (
                <div className="px-4 pb-4 border-t border-gray-200 pt-3 space-y-3">
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
                    {Object.entries(s.answers || {}).map(([k, v]) => (
                      <p key={k} className="text-xs text-gray-600">
                        <span className="font-semibold text-gray-700">{(labels[s.form] || {})[k] || k}:</span>{' '}
                        {Array.isArray(v) ? v.join(', ') : typeof v === 'boolean' ? (v ? 'Sì' : 'No') : String(v)}
                      </p>
                    ))}
                    {(s.allegati || []).length > 0 && (
                      <p className="text-xs text-gray-600"><span className="font-semibold text-gray-700">Allegati:</span> {s.allegati.join(', ')} (ricevuti via email)</p>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-gray-500">Stato:</label>
                      <select data-testid={`sub-stato-${s.id}`} value={s.stato || 'nuova'} onChange={(e) => setStato(s.id, e.target.value)}
                        className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs">
                        {Object.entries(STATO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                    <button data-testid={`sub-delete-${s.id}`} type="button" onClick={() => deleteSub(s.id)}
                      className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-red-500">
                      <Trash2 size={12} /> Elimina (GDPR)
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
